import { usePlan } from 'data/plan/usePlan';
import { useRedirect } from 'data/redirect/useRedirect';
import { useTranslation } from 'data/translation/useTranslation';
import { ErrorType, makeErrorFromApi } from 'lib/error';
import { setHomeId, setUserType } from 'lib/tracking';
import { buildUrlStrict } from 'lib/url';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { InviteToken } from 'types';
import inviterApi from 'data/inviter/api';
import * as redux from 'data/inviter/redux';
import { useError } from 'data/error/useError';
import { isErrorOfTypeAndCode } from 'data/error/utils';

export const LOAD_INVITER_ERROR_TYPE: ErrorType = 'LOAD_INVITER_ERROR_TYPE';

export const hasRestriction = (
  inviterData: redux.UserData | null,
  reason: redux.RestrictionReason,
) =>
  inviterData?.joinRestrictions?.some(
    (restriction: redux.JoinRestriction) => restriction.reason === reason,
  ) || false;

export const loadInviter =
  (dispatch: Dispatch) => async (inviteToken: InviteToken) => {
    dispatch(redux.loadInviterData());
    try {
      const data = await inviterApi.loadInviter(inviteToken);
      setHomeId(data.homeId);
      setUserType(data.isLoggedInUserMaster ? 'master' : 'sub');
      dispatch(redux.setInviterData(data));
    } catch (e: any) {
      // Resolve the home to empty string in case of an error
      setUserType('');
      setHomeId('');
      dispatch(redux.setInviterData(null));

      throw makeErrorFromApi(LOAD_INVITER_ERROR_TYPE, e);
    }
  };

const useInviter = () => {
  const { locale } = useTranslation();
  const { planType } = usePlan();
  const dispatch = useDispatch();
  const inviterState = useSelector(redux.selector);
  const { error, setError } = useError();

  const { query } = useRouter();
  const inviteToken = Array.isArray(query.token)
    ? query.token[0]
    : query.token!;

  useEffect(() => {
    if (!inviterState.data) {
      loadInviter(dispatch)(inviteToken).catch(setError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useRedirect({
    route: buildUrlStrict(planType, 'HOME_HUB', locale),
    condition: isErrorOfTypeAndCode(
      error,
      LOAD_INVITER_ERROR_TYPE,
      'USER_ALREADY_JOINED',
    ),
  });

  return {
    inviteToken,
    isLoadingInviter: inviterState.isLoading,
    inviterData: inviterState.data,
  };
};

export default useInviter;
